import { Paper } from '@mui/material';
import { ErrorMessage, Form, Formik } from 'formik';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';
import { array, boolean, object, string, type Schema } from 'yup';

import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import RoundedButton from '@components/RoundedButton/index';

import {
  useCreateLegalDirective,
  useGetLegalDirectives,
  useUpdateLegalDirective,
} from '@hooks/api/legal-directive';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import { getLegalDirectiveTemplate } from '@models/fhir-templates/consent';
import type { CategoryType } from '@models/legal-directive';

import { getFhirDate } from '@utils/fhir';
import { displayNameMap } from '@utils/legalDirective';

import type { Extension } from 'generated/types';

import LegalSelector from './LegalSelector/LegalSelector';
import UpdatedTimestamp from './UpdatedTimestamp';

const messages = defineMessages({
  save: {
    id: 'GLOBAL.save',
    defaultMessage: 'Save',
  },
  errorMessage: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  success: {
    id: 'legalMain.success',
    defaultMessage: '{type} have been updated',
  },
  select: {
    id: 'legalMain.select',
    defaultMessage: 'Please select at least one option',
  },
});

export type FormValues = {
  selections: Pick<Extension, 'url' | 'valueBoolean'>[];
};

interface LegalSurveryProps {
  questionId: CategoryType;
}

function LegalSurvey({ questionId }: LegalSurveryProps) {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { flags } = useFeatureFlags();
  const {
    state: { activeProfile },
  } = useSession();
  const [patient_id] = [activeProfile?.patient_id];

  const { data: directivesData, loading: directiveLoading } =
    useGetLegalDirectives();
  const [createConsent, createConsentLoading] = useCreateLegalDirective();
  const [updateConsent, updateConsentLoading] = useUpdateLegalDirective();

  const loading =
    directiveLoading || updateConsentLoading || createConsentLoading;

  const directive = directivesData.find(
    ({ category }) => category === questionId
  );

  const initialValues: FormValues = {
    selections: directive?.selections ?? [],
  };

  const validationSchema: Schema<FormValues> = object().shape({
    selections: array()
      .of(
        object().shape({
          url: string().required(),
          valueBoolean: boolean().nullable(),
        })
      )
      .min(1, formatMessage(messages.select))
      .default([]),
  });

  async function handleSubmit(values: FormValues) {
    if (!patient_id) {
      return;
    }
    try {
      const existingDirective = directive?.getModelInput();

      if (directive?.id && existingDirective) {
        await updateConsent({
          input: {
            consent_id: directive.id,
            consent: {
              ...existingDirective,
              extension: values.selections,
            },
          },
        });
      } else {
        await createConsent({
          input: {
            ...getLegalDirectiveTemplate(
              patient_id,
              questionId,
              values.selections
            ),
          },
        });
      }
    } catch {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.errorMessage),
      });
    } finally {
      enqueueSnackbar({
        variant: 'success',
        message: formatMessage(messages.success, {
          type:
            questionId in displayNameMap
              ? displayNameMap[questionId]
              : 'Legal/Directives',
        }),
      });
    }
  }

  return (
    <ContentLoader loading={loading}>
      <Paper
        sx={{
          width: '100%',
          padding: 2,
        }}
      >
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ isSubmitting }) => {
            return (
              <Form>
                <UpdatedTimestamp
                  lastUpdate={
                    directive?.lastUpdate
                      ? getFhirDate(directive.lastUpdate)
                      : null
                  }
                  loading={loading}
                />
                <LegalSelector type={questionId} />

                <ErrorMessage
                  name="selections"
                  render={(e) => {
                    if (typeof e !== 'string') {
                      return null;
                    }
                    return <p className="error-msg">{e}</p>;
                  }}
                />
                <RoundedButton
                  disabled={
                    isSubmitting ||
                    loading ||
                    !flags.isLegalDirectiveCreateConsentEnabled ||
                    !flags.isLegalDirectiveUpdateConsentEnabled
                  }
                  type="submit"
                >
                  {formatMessage(messages.save)}
                </RoundedButton>
              </Form>
            );
          }}
        </Formik>
      </Paper>
    </ContentLoader>
  );
}

export default withErrorBoundary(LegalSurvey, {
  FallbackComponent: ErrorBoundaryFallback,
});
